import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Row, Col, Button, Typography, Space, Empty, Grid } from 'antd'
import {
  PlusCircleOutlined,
  RocketOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { fetchTaskStats, queryTasks } from '../store/slices/taskSlice'
import TaskCard from '../components/Task/TaskCard'
import Loading from '../components/Common/Loading'
import './Dashboard.css'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const Dashboard = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { taskStats, tasks, loading } = useSelector((state) => state.task)
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const visibleTaskTotal =
    (taskStats.pending || 0) +
    (taskStats.running || 0) +
    (taskStats.completed || 0)

  useEffect(() => {
    dispatch(fetchTaskStats())
    // Query recent 9 tasks using pagination API
    dispatch(queryTasks({
      page: 0,
      pageSize: 9,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    }))
  }, [dispatch])

  const handleNewAnalysis = () => {
    navigate('/tasks/new')
  }

  const handleViewHistory = () => {
    navigate('/tasks')
  }

  const handleCardClick = (status) => {
    if (status) {
      navigate(`/tasks?status=${status}`)
    } else {
      navigate('/tasks')
    }
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className="dashboard-page">
      {/* Hero Section */}
      <div className="dashboard-hero">
        <div className="dashboard-hero__content">
          <Space direction="vertical" size={8} className="dashboard-hero__text">
            <Title level={1} className="dashboard-hero__title">
              Welcome to Quantum Husky
            </Title>
            <Text className="dashboard-hero__subtitle">
              AI-powered trading analysis at your fingertips
            </Text>
          </Space>
          <Button
            type="default"
            size="large"
            icon={<RocketOutlined />}
            onClick={handleNewAnalysis}
            className="dashboard-hero__cta"
          >
            Start New Analysis
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="dashboard-stats">
        <Col xs={12} sm={12} lg={6}>
          <Card
            bordered={false}
            className="dashboard-stat-card dashboard-stat-card--gradient dashboard-stat-card--clickable"
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
            onClick={() => handleCardClick('')}
            style={{ cursor: 'pointer' }}
          >
            <Space direction="vertical" size={2} style={{ width: '100%', textAlign: isMobile ? 'center' : 'left' }}>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 500 }}>
                Total Analyses
              </Text>
              <Title level={2} style={{ color: '#fff', margin: '4px 0', fontSize: '32px', fontWeight: 700 }}>
                {visibleTaskTotal}
              </Title>
              {!isMobile && (
                <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                  All time
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card
            bordered={false}
            className="dashboard-stat-card dashboard-stat-card--warning dashboard-stat-card--clickable"
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
            onClick={() => handleCardClick('PENDING')}
            style={{ cursor: 'pointer' }}
          >
            <Space direction="vertical" size={2} style={{ width: '100%', textAlign: isMobile ? 'center' : 'left' }}>
              <Space size={6} style={{ justifyContent: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                <ClockCircleOutlined style={{ fontSize: 16, color: '#f59e0b' }} />
                <Text style={{ color: '#78716c', fontSize: 13, fontWeight: 500 }}>
                  Pending
                </Text>
              </Space>
              <Title level={2} style={{ color: '#292524', margin: '4px 0', fontSize: '32px', fontWeight: 700 }}>
                {taskStats.pending || 0}
              </Title>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Waiting to start
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card
            bordered={false}
            className="dashboard-stat-card dashboard-stat-card--info dashboard-stat-card--clickable"
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
            onClick={() => handleCardClick('RUNNING')}
            style={{ cursor: 'pointer' }}
          >
            <Space direction="vertical" size={2} style={{ width: '100%', textAlign: isMobile ? 'center' : 'left' }}>
              <Space size={6} style={{ justifyContent: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                <RocketOutlined style={{ fontSize: 16, color: '#3b82f6' }} />
                <Text style={{ color: '#78716c', fontSize: 13, fontWeight: 500 }}>
                  Running
                </Text>
              </Space>
              <Title level={2} style={{ color: '#292524', margin: '4px 0', fontSize: '32px', fontWeight: 700 }}>
                {taskStats.running || 0}
              </Title>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Currently processing
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card
            bordered={false}
            className="dashboard-stat-card dashboard-stat-card--success dashboard-stat-card--clickable"
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
            onClick={() => handleCardClick('COMPLETED')}
            style={{ cursor: 'pointer' }}
          >
            <Space direction="vertical" size={2} style={{ width: '100%', textAlign: isMobile ? 'center' : 'left' }}>
              <Space size={6} style={{ justifyContent: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                <CheckCircleOutlined style={{ fontSize: 16, color: '#10b981' }} />
                <Text style={{ color: '#78716c', fontSize: 13, fontWeight: 500 }}>
                  Completed
                </Text>
              </Space>
              <Title level={2} style={{ color: '#292524', margin: '4px 0', fontSize: '32px', fontWeight: 700 }}>
                {taskStats.completed || 0}
              </Title>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Successfully analyzed
                </Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Tasks Section */}
      {(() => {
        const content = (
          <div className="dashboard-recent-content">
            <div className="dashboard-recent-header">
              <span className="dashboard-recent-title">
                Recent Analyses
              </span>
              {tasks.length > 0 && (
                <Button type="link" onClick={handleViewHistory} className="dashboard-recent-link">
                  View All →
                </Button>
              )}
            </div>

            {tasks.length === 0 ? (
              <div style={{ padding: isMobile ? '24px 0' : '60px 0', textAlign: 'center' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" size={16}>
                      <Text style={{ fontSize: 16, color: '#78716c' }}>
                        No analyses yet. Start your first stock analysis now!
                      </Text>
                      <Button
                        type="primary"
                        size="large"
                        icon={<PlusCircleOutlined />}
                        onClick={handleNewAnalysis}
                        style={{
                          height: 48,
                          padding: isMobile ? '0 20px' : '0 32px',
                          fontSize: isMobile ? 15 : 16,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          width: isMobile ? '100%' : 'auto',
                        }}
                      >
                        Create First Analysis
                      </Button>
                    </Space>
                  }
                />
              </div>
            ) : isMobile ? (
              <div className="dashboard-recent-list">
                {tasks.map((task) => (
                  <TaskCard key={task.taskId || task.task_id || task.id} task={task} />
                ))}
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {tasks.map((task) => (
                  <Col xs={24} sm={12} lg={8} key={task.taskId || task.task_id || task.id}>
                    <TaskCard task={task} />
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )

        if (isMobile) {
          return (
            <div className="dashboard-recent-mobile">
              {content}
            </div>
          )
        }

        return (
          <Card
            bordered={false}
            className="dashboard-recent-card"
            bodyStyle={{ padding: 32 }}
          >
            {content}
          </Card>
        )
      })()}
    </div>
  )
}

export default Dashboard
